import Fuse from 'fuse.js';
import { useMemo } from 'preact/hooks';
import { create } from 'zustand';
import { Episode } from '../../models/podcasts';
import { checkIfPlayed } from '../../utils/check-if-played';

export type FilterStoreState = {
  played: 'all' | 'played' | 'unplayed';
  season: number | 'all';
  search: string;
  sort: 'newest' | 'oldest';
};

type FilterActions = {
  setPlayed: (played: 'all' | 'played' | 'unplayed') => void;
  setSeason: (season: number | 'all') => void;
  setSearch: (search: string) => void;
  setSort: (sort: 'newest' | 'oldest') => void;
  setAll: (filters: FilterStoreState) => void;
  reset: () => void;
};

export const useFilterStore = create<FilterStoreState & { actions: FilterActions }>()(
  (set, get) => ({
    played: 'all',
    season: 'all',
    search: '',
    sort: 'newest',
    actions: {
      setPlayed: (played) => set({ played }),
      setSeason: (season) => set({ season }),
      setSearch: (search) => set({ search: search.trim() }),
      setSort: (sort) => set({ sort }),
      setAll: (filters) => set(filters),
      reset: () => set({ played: 'all', season: 'all', search: '', sort: 'newest' }),
    },
  })
);

export const filterActions = useFilterStore.getState().actions;

export function useFilteredAndSortedEpisodes(
  allEpisodes: Episode[],
  filters: FilterStoreState
) {
  // Defaults, no filter/sorting needed
  if (
    filters.played === 'all' &&
    filters.season === 'all' &&
    !filters.search &&
    filters.sort === 'newest'
  ) {
    return allEpisodes;
  }

  const matchesPlayedFilter =
    filters.played === 'all'
      ? () => true
      : filters.played === 'played'
        ? (ep: Episode) => checkIfPlayed(ep)
        : (ep: Episode) => !checkIfPlayed(ep);

  const matchesSeasonFilter =
    filters.season === 'all'
      ? () => true
      : (ep: Episode) => ep.season === filters.season;

  const episodes: (Episode & { fuseScore?: number })[] = [];
  for (const ep of allEpisodes) {
    if (!matchesPlayedFilter(ep)) continue;
    if (!matchesSeasonFilter(ep)) continue;
    episodes.push(ep);
  }

  // If search is set, do a fuzzy search
  if (filters.search) {
    const fuse = new Fuse(episodes, {
      keys: ['title', 'excerpt'],
      ignoreLocation: true,
      minMatchCharLength: 2,
      includeScore: true,
    });
    const results = fuse.search(filters.search);
    // console.log(
    //   episodes.length,
    //   results.map((r) => [r.score, r.item])
    // );
    return results.filter((result) => result.score! < 0.5).map((result) => result.item);
  }

  // Fine to mutate since we're not using the original array
  if (filters.sort === 'oldest') episodes.reverse();

  return episodes;
}

export function isFilterApplied(filters: FilterStoreState) {
  return filters.played !== 'all' || filters.season !== 'all' || filters.search;
}

export function useSeasons(episodes: Episode[]) {
  return useMemo(() => {
    const seasonsSet = new Set<number>();
    let episodeWithSeasonCount = 0;
    for (const ep of episodes) {
      if (ep.season) {
        seasonsSet.add(ep.season);
        episodeWithSeasonCount++;
      }
    }
    // Only return seasons if there's more than one
    if (seasonsSet.size <= 1) return null;
    // Only return seasons if most episodes have a season
    if (episodeWithSeasonCount <= episodes.length / 2) return null;
    // Show seasons filter
    return Array.from(seasonsSet).sort((a, b) => a - b);
  }, [episodes]);
}
